import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import ProgramFollowTile from './ProgramFollowTile';

export default function ProgramFollowCard(props) {
  const { variant, tiles, onClickTile, onClickDelete, onClickAction } = props;

  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const ScrollAreaRef = useRef(null);

  const isFollowVariant = variant === 'follow';

  const chipColors = {
    background: isFollowVariant ? theme.subKeyAlpha50 : theme.primaryAlpha20,
    border: isFollowVariant ? theme.subKey : theme.primary,
    dot: isFollowVariant ? theme.subKey : theme.primary
  };

  const chipLabel = isFollowVariant ? t('myStoreSetting.program.chipFollow') : t('myStoreSetting.program.chipFollower');
  const cardTitle = isFollowVariant ? t('myStoreSetting.program.followTitle') : t('myStoreSetting.program.followerTitle');
  const cardDescription = isFollowVariant ? t('myStoreSetting.program.followDescription') : t('myStoreSetting.program.followerDescription');
  const actionLabel = isFollowVariant ? t('myStoreSetting.program.findFollow') : t('myStoreSetting.program.manageFollow');

  // 데이터 변경 / 마운트 시 인디케이터 초기화 //
  useEffect(() => {
    recalcIndicator();
  }, [tiles])

  // 윈도우 리사이즈 시 인디케이터 재계산 //
  useEffect(() => {
    window.addEventListener('resize', recalcIndicator);

    return () => window.removeEventListener('resize', recalcIndicator);
  }, [])

  // 터치패드 세로 제스처를 가로 스크롤로 변환 (passive: false 로 preventDefault 가능) //
  useEffect(() => {
    const el = ScrollAreaRef.current;

    if (!el) return;

    const handler = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      e.preventDefault();
      el.scrollLeft += e.deltaY * 0.5;
    };

    el.addEventListener('wheel', handler, { passive: false });

    return () => el.removeEventListener('wheel', handler);
  }, [])

  // 가로 스크롤 인디케이터 비율 / 위치 계산 //
  function recalcIndicator() {
    const el = ScrollAreaRef.current;

    if (!el) return;

    const { scrollWidth, clientWidth, scrollLeft } = el;

    if (scrollWidth <= clientWidth) {
      setIsScrollable(false);
      return;
    }

    const widthPct = (clientWidth / scrollWidth) * 100;
    const maxScroll = scrollWidth - clientWidth;
    const leftPct = (scrollLeft / maxScroll) * (100 - widthPct);

    setIsScrollable(true);
    setThumbWidth(widthPct);
    setThumbLeft(leftPct);
  }

  return (
    <CardWrapper backgroundColor={theme.subBackground}>
      <ChipWrapper>
        <Chip
          backgroundColor={chipColors.background}
          borderColor={chipColors.border}>
          <ChipDot backgroundColor={chipColors.dot} />
          <ChipText color={theme.text}>{t('myStoreSetting.program.chipPrefix')}</ChipText>
          <ChipText color={theme.text}>·</ChipText>
          <ChipText color={theme.text}>{chipLabel}</ChipText>
        </Chip>
      </ChipWrapper>
      <TextBlock>
        <TitleText color={theme.text}>{cardTitle}</TitleText>
        <DescriptionText color={theme.subText}>{cardDescription}</DescriptionText>
      </TextBlock>
      <ScrollSection>
        <TileScrollArea
          ref={ScrollAreaRef}
          isEmpty={tiles.length === 0}
          onScroll={recalcIndicator}>
          {
            tiles.length === 0 &&
            <EmptyText color={theme.subText}>{isFollowVariant ? t('myStoreSetting.program.noFollow') : t('myStoreSetting.program.noFollower')}</EmptyText>
          }
          {
            tiles.length > 0 &&
            tiles.map((data, index) => (
              <ProgramFollowTile
                key={'ProgramFollowTile-' + variant + '-' + index}
                data={data}
                //onClickTile={onClickTile}
                onClickDelete={onClickDelete} />
            ))
          }
        </TileScrollArea>
        <IndicatorTrack lineColor={theme.line}>
          <IndicatorThumb
            thumbColor={theme.scrollBar}
            thumbWidth={thumbWidth}
            thumbLeft={thumbLeft}
            isVisible={isScrollable} />
        </IndicatorTrack>
      </ScrollSection>
      <FooterWrapper>
        <CountWrapper>
          <CountNumberText color={theme.text}>{tiles.length}</CountNumberText>
          <CountUnitText color={theme.text}>{t('myStoreSetting.program.programCount')}</CountUnitText>
        </CountWrapper>
        <ActionText
          color={theme.keyText}
          onClick={onClickAction}>{actionLabel}</ActionText>
      </FooterWrapper>
    </CardWrapper>
  );
}

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1 0 0;
  min-width: 0;

  padding: 20px;

  gap: 10px;

  border-radius: 10px;

  background-color: ${props => props.backgroundColor};
`;

const ChipWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const Chip = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 2px 10px;

  gap: 5px;

  border: 1px solid ${props => props.borderColor};
  border-radius: 100px;

  background-color: ${props => props.backgroundColor};
`;

const ChipDot = styled.div`
  width: 5px;
  height: 5px;

  border-radius: 100px;

  background-color: ${props => props.backgroundColor};
`;

const ChipText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  white-space: nowrap;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  gap: 5px;

  width: 100%;
`;

const TitleText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const DescriptionText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const ScrollSection = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding-top: 10px;

  gap: 10px;

  width: 100%;
`;

const TileScrollArea = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: ${props => props.isEmpty ? 'center' : 'flex-start'};
  align-items: center;

  gap: 10px;

  width: 100%;
  min-height: 100px;

  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const IndicatorTrack = styled.div`
  position: relative;

  width: 100%;
  height: 1px;

  border-radius: 30px;

  background-color: ${props => props.lineColor};
`;

const IndicatorThumb = styled.div`
  position: absolute;
  top: 0;

  height: 4px;

  border-radius: 30px;

  background-color: ${props => props.thumbColor};

  width: ${props => props.thumbWidth}%;
  left: ${props => props.thumbLeft}%;

  opacity: ${props => props.isVisible ? 1 : 0};

  transition: opacity 0.2s ease-in-out;
`;

const FooterWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: baseline;

  width: 100%;
`;

const CountWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: baseline;

  gap: 10px;
`;

const CountNumberText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 50px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const CountUnitText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const ActionText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }

  transition: all 0.3s ease-in-out;
`;

const EmptyText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;
